import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { pollGroupDownloadStatus } from "../../actions/invoiceActions";
import {
  GROUP_DOWNLOAD_BUNDLE_DOWNLOADED,
  GROUP_DOWNLOAD_JOB_REHYDRATE,
} from "../../actions/constants";
import { getGroupDownloadBundleFileUrl } from "../../api/index";
import {
  saveGroupDownloadState,
  loadGroupDownloadState,
  clearGroupDownloadState,
} from "../../utils/groupDownloadStorage";

const POLL_INTERVAL_MS = 2500;

const triggerBrowserDownload = (url, filename) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

// Non-visual component mounted once at the app root (outside the router
// Switch) so polling + auto-download keep running even while the user
// navigates to other pages — job progress lives in Redux, not in any single
// page's state, and a minimal resume snapshot lives in localStorage so a
// hard page refresh doesn't lose track of an in-progress job or re-trigger
// already-downloaded bundles.
const GroupDownloadPoller = () => {
  const dispatch = useDispatch();
  const job = useSelector((state) => state.groupDownloadJob);
  const intervalRef = useRef(null);
  const rehydratedRef = useRef(false);

  useEffect(() => {
    if (rehydratedRef.current) return;
    rehydratedRef.current = true;

    if (!job.jobId) {
      const saved = loadGroupDownloadState();
      if (saved?.jobId) {
        dispatch({ type: GROUP_DOWNLOAD_JOB_REHYDRATE, payload: saved });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (job.jobId && job.status === "processing") {
      saveGroupDownloadState({ jobId: job.jobId, downloadedBundles: job.downloadedBundles });
    } else if (job.status === "completed" || job.status === "failed") {
      clearGroupDownloadState();
    }
  }, [job.jobId, job.status, job.downloadedBundles]);

  useEffect(() => {
    if (!job.jobId || !job.bundles?.length) return;

    job.bundles.forEach((bundle) => {
      if (bundle.status === "ready" && !job.downloadedBundles.includes(bundle.bundleNumber)) {
        dispatch({
          type: GROUP_DOWNLOAD_BUNDLE_DOWNLOADED,
          payload: { bundleNumber: bundle.bundleNumber },
        });
        triggerBrowserDownload(
          getGroupDownloadBundleFileUrl(job.jobId, bundle.bundleNumber),
          `Invoices_${job.jobId}_bundle${bundle.bundleNumber}.zip`
        );
      }
    });
  }, [job.jobId, job.bundles, job.downloadedBundles, dispatch]);

  useEffect(() => {
    if (job.jobId && job.status === "processing") {
      intervalRef.current = setInterval(() => {
        dispatch(pollGroupDownloadStatus(job.jobId));
      }, POLL_INTERVAL_MS);
    }

    return () => clearInterval(intervalRef.current);
  }, [job.jobId, job.status, dispatch]);

  return null;
};

export default GroupDownloadPoller;
